/**
 * buildChainFromGraph — Build a HydraChain by walking the graph upstream.
 *
 * Instead of propagating chain metadata through payloads, this queries
 * GraphIndex to discover the Hydra pipeline and builds the chain in one pass.
 */

import type { GraphIndex, GraphEdge, GraphNode } from '@/runtime/GraphIndex';
import type { HydraChain, HydraFunctionMeta } from './HydraEndpoint';
import type { HydraSourceNode } from './nodes/HydraSourceNode';
import type { HydraEffectNode } from './nodes/HydraEffectNode';
import type { HydraCompositeNode } from './nodes/HydraCompositeNode';
import { getVideoEntry } from '@plugins/video/videoRegistry';
import { logger } from '@/logging';

type HydraNode = { getHydraMeta(): HydraFunctionMeta };

function isHydraNode(node: any): node is HydraNode {
  return typeof node?.getHydraMeta === 'function';
}

/**
 * Check if a graph node has a video canvas (e.g. Three.js camera).
 * Returns a newSource meta if found, null otherwise.
 */
function getVideoSourceMeta(gn: GraphNode): HydraFunctionMeta | null {
  const entry = getVideoEntry(gn.instancePath, gn.nodeId);
  const canvas = entry?.getCanvas();
  if (canvas) {
    return { newSource: { type: 'canvas', data: canvas } };
  }
  return null;
}

/**
 * Build a HydraChain by walking upstream from the given node.
 *
 * @param graphIndex  The graph index to query
 * @param nodeId      Starting node ID (typically the output endpoint's node)
 * @param instancePath  Instance path of the starting node
 * @param inputEndpointId  Optional: which input endpoint to trace upstream from.
 *                         If not specified, uses the first hydra input edge found.
 */
export function buildChainFromGraph(
  graphIndex: GraphIndex,
  nodeId: string,
  instancePath: string,
  inputEndpointId?: string
): HydraChain {
  const startKey = graphIndex.nodeKey(instancePath, nodeId);
  return buildChainFromKey(graphIndex, startKey, inputEndpointId);
}

/**
 * Recursive chain builder working with graph node keys.
 */
function buildChainFromKey(
  graphIndex: GraphIndex,
  nodeKey: string,
  inputEndpointId?: string
): HydraChain {
  const gn = graphIndex.getNodeByKey(nodeKey);
  if (!gn) {
    // Composite/definition boundary node — not registered in GraphIndex but edges exist.
    // Traverse upstream through its edges.
    return buildUpstreamChain(graphIndex, nodeKey, inputEndpointId);
  }

  const node = gn.node;

  // If this node is a hydra node, handle it based on type
  if (isHydraNode(node)) {
    const meta = node.getHydraMeta();

    if (meta.source || meta.newSource) {
      // Source node — no upstream, this is the chain start
      return [meta];
    }

    if (meta.effect) {
      // Effect node — build upstream chain, then append this effect
      const upstreamChain = buildUpstreamChain(graphIndex, nodeKey, 'hydraIn');
      if (upstreamChain.length === 0) return [];
      return [...upstreamChain, meta];
    }

    if (meta.composite) {
      // Composite node — primary chain from hydraIn1, secondary from hydraIn2
      const primaryChain = buildUpstreamChain(graphIndex, nodeKey, 'hydraIn1');
      const secondaryChain = buildUpstreamChain(graphIndex, nodeKey, 'hydraIn2');

      if (primaryChain.length === 0 || secondaryChain.length === 0) return [];

      // Insert secondary chain as first arg (same as the old propagation pattern)
      const compositeMeta: HydraFunctionMeta = {
        ...meta,
        args: [secondaryChain, ...(meta.args || [])]
      };
      return [...primaryChain, compositeMeta];
    }
  }

  // Not a hydra node — check if it's a video source (e.g. Three.js camera).
  // If the node has a canvas in the video registry, use it as a chain source.
  const videoSource = getVideoSourceMeta(gn);
  if (videoSource) return [videoSource];

  // Otherwise traverse upstream through any input edge.
  // Don't filter by inputEndpointId: it may carry a source endpoint name
  // (e.g. 'hydraOut') from edge traversal that doesn't match an input on this node.
  return buildUpstreamChain(graphIndex, nodeKey);
}

/**
 * Walk upstream from a node through a specific target endpoint,
 * building the chain from what's connected to that endpoint.
 */
function buildUpstreamChain(
  graphIndex: GraphIndex,
  nodeKey: string,
  targetEndpointId?: string
): HydraChain {
  const edges = graphIndex.getReverseEdges(nodeKey) ?? [];

  // Filter edges to only those targeting the specified endpoint
  const relevantEdges = targetEndpointId
    ? edges.filter(e => e.targetEndpointId === targetEndpointId)
    : edges;

  if (relevantEdges.length === 0) return [];

  // For hydra chains, there should be exactly one upstream connection per input
  const edge = relevantEdges[0];
  // Pass sourceEndpointId so composite boundaries know which endpoint to follow
  return buildChainFromKey(graphIndex, edge.sourceKey, edge.sourceEndpointId);
}
